/**
 * Live staff handoff for the AI assistant chat.
 *
 * Contract: thoughts/shared/plans/2026-09-29-assistant-live-staff-handoff.md
 * (monorepo root). equility-api owns the `assistant_handoffs` table and its
 * state machine; reconciliation-ai owns the messages. These types are the web's
 * view of both, after `services/assistantHandoffApi.ts` has normalized the
 * wire casing.
 */

export type HandoffStatus =
	| "AWAITING_HUMAN"
	| "HUMAN_ACTIVE"
	| "RETURNED_TO_AI"
	| "RESOLVED"
	| "CONVERTED_TO_TICKET"
	| "CANCELLED";

export type HandoffTrigger = "BUTTON" | "CHAT_REQUEST";

/** Open means someone is (or should be) on the way. At most one per session. */
export const OPEN_HANDOFF_STATUSES: readonly HandoffStatus[] = [
	"AWAITING_HUMAN",
	"HUMAN_ACTIVE",
];

export interface IHandoffPerson {
	id: number;
	firstName?: string | null;
	lastName?: string | null;
	email?: string | null;
}

export interface IAssistantHandoff {
	id: string;
	sessionId: string;
	userId: number | null;
	companyId: number | null;
	status: HandoffStatus;
	trigger: HandoffTrigger;
	reason: string | null;
	assignedStaffUserId: number | null;
	/** Display name of the assignee when the API includes one. */
	assignedStaffName: string | null;
	requestedAt: string | null;
	claimedAt: string | null;
	releasedAt: string | null;
	closedAt: string | null;
	supportTicketId: number | null;
	/** Human ticket reference (e.g. "TKT-00025") when the API sends one. */
	supportTicketNumber?: string | null;
	/** Who cancelled: "customer", or "system" when the chat ended first. */
	cancelSource?: string | null;
	createdAt: string | null;
	updatedAt: string | null;
	/** Present on admin responses when the API joins them. */
	user?: IHandoffPerson | null;
	company?: { id: number; name: string } | null;
}

/**
 * Who wrote a message. `staff` rides role ASSISTANT in reconciliation-ai with
 * `structured_data.author = "staff"`; `system` is an injected notice
 * (`staff_joined`, `staff_left`, `resolved`, `converted_to_ticket`).
 */
export type ChatAuthor = "user" | "assistant" | "staff" | "system";

export interface ITranscriptMessage {
	id: string;
	author: ChatAuthor;
	content: string;
	timestamp: string;
	staffName: string | null;
	kind: string | null;
}

export interface IHandoffDetail {
	handoff: IAssistantHandoff;
	messages: ITranscriptMessage[];
	/** The API withheld the transcript from this staff member. */
	transcriptHidden?: boolean;
}

/** `chat:handoff:error` socket payload (customer). */
export interface IHandoffErrorSocketEvent {
	sessionId: string;
	error: unknown;
}

/** `chat:handoff` socket payload (customer and staff rooms). */
export interface IHandoffSocketEvent {
	sessionId: string;
	handoff: unknown;
}

/** `chat:message` socket payload. */
export interface IChatMessageSocketEvent {
	sessionId: string;
	message: unknown;
}
